import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Radio, Tabs, TabsProps, Tooltip, Popconfirm } from 'antd';
import useModal from 'hooks/useModal';
import { CreateMarketingTaskRoundBody } from 'services/api/oms';
import useMixingTokoText from 'hooks/business/useMixingTokoText';
import { scenarioTypeI18nLabelMap } from './config';
import { useGetTimes } from './RoundForm';

export interface RoundTabsProps extends TabsProps {
  editable?: boolean;
  scenarioType: Marketing.AllScenarioType;
  task: Marketing.Task;
  policyList: Marketing.Policy[];
  roundLimit?: number;
  onRoundDelete: (id: Marketing.Round['id']) => Promise<any>;
  onRoundCreate: (body: CreateMarketingTaskRoundBody) => Promise<any>;
}

export type OrderBy = Marketing.PolicyOrderBy;

export const orderList: OrderBy[] = ['priorityAsc', 'priorityDesc'];

const Tab = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  .anticon {
    padding-left: 10px;
    margin: 0;
    color: #515359;
  }
`;

const StyledTabs = styled(Tabs)<{ appendIndex: number }>`
  .${({ theme }) => theme.getAntdPrefixedClassName('tabs-extra-content')} {
    padding: 0 0 5px;
  }
`;

const DisabledClose = styled(CloseOutlined)`
  color: var(--theme-fill-4) !important;
  cursor: not-allowed;
`;

const PopoverTitle = styled.div`
  margin-left: -14px;
`;

function RoundTabs(props: RoundTabsProps) {
  const {
    editable = true,
    scenarioType,
    activeKey,
    task: { id: taskId, rounds },
    policyList,
    roundLimit = 4,
    onRoundDelete,
    onRoundCreate,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const { getTimes } = useGetTimes();

  const {
    orderReminderTitle: { text: orderReminderTitle },
  } = useMixingTokoText();

  const scenarioLabel =
    scenarioType === 'ORDER_REMINDER'
      ? orderReminderTitle
      : (t(scenarioTypeI18nLabelMap[scenarioType]) as string);

  const { visible, show, hide, meta, update } = useModal<boolean>(false, true);

  const handleAddRoundClick = () => {
    if (rounds.length > 0) {
      show(true);
      return;
    }

    onRoundCreate({
      executeTime: 5,
      executeTimeUnit: '1',
      taskId,
    });
  };

  return (
    <StyledTabs
      appendIndex={rounds.length < roundLimit ? rounds.length + 1 : 0}
      activeKey={activeKey}
      type="card"
      {...otherProps}
    >
      {rounds.map((round) => {
        const { id: roundId, round: roundIndex } = round;

        const deleteDisabled = rounds.length === 1;

        const del = deleteDisabled ? (
          <Tooltip title={t('marketing.第1次不支持删除')}>
            <DisabledClose />
          </Tooltip>
        ) : (
          <Popconfirm
            title={t('marketing.您确定删除第x次action吗', {
              x: getTimes(roundIndex),
              action: scenarioLabel,
            })}
            onConfirm={(e) => {
              e?.stopPropagation();
              onRoundDelete(roundId);
            }}
            onCancel={(e) => {
              e?.stopPropagation();
            }}
          >
            <CloseOutlined
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </Popconfirm>
        );

        return (
          <Tabs.TabPane
            key={`${roundId}`}
            closable
            tab={
              <Tab>
                <span>
                  {roundLimit === 1
                    ? scenarioLabel
                    : t('marketing.第x次action', {
                        x: getTimes(roundIndex),
                        action: scenarioLabel,
                      })}
                </span>
                {editable && del}
              </Tab>
            }
          />
        );
      })}
      {rounds.length < roundLimit && editable && (
        <Tabs.TabPane
          key="append"
          tab={
            <Popconfirm
              visible={visible}
              onVisibleChange={(visible) => update(visible, meta)}
              icon={false}
              title={
                <PopoverTitle>
                  <h4>
                    {t('marketing.新增第x次action', {
                      x: getTimes(rounds.length + 1),
                      action: scenarioLabel,
                    })}
                  </h4>
                  <Radio checked={meta} onChange={(e) => update(visible, e.target.checked)}>
                    {t('marketing.沿用第x次策略', { x: rounds.length })}
                  </Radio>
                  <br />
                  <Radio checked={!meta} onChange={(e) => update(visible, !e.target.checked)}>
                    {t('marketing.自定义策略')}
                  </Radio>
                </PopoverTitle>
              }
              onConfirm={(e) => {
                e?.stopPropagation();

                onRoundCreate({
                  executeTime: 10,
                  executeTimeUnit: '1',
                  taskId,
                  copyRoundId: meta ? rounds[rounds.length - 1].id : undefined,
                }).finally(() => hide());
              }}
              onCancel={(e) => e?.stopPropagation()}
            >
              <div onClick={handleAddRoundClick} data-guide="marketing-guide-step-3">
                <PlusOutlined />
                <span>{t('marketing.新增scenario次数', { scenario: scenarioLabel })}</span>
              </div>
            </Popconfirm>
          }
        />
      )}
    </StyledTabs>
  );
}

export default RoundTabs;
